<template>
    <form @submit.prevent="submit" class="ppv2-p-8">
        <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-8">
            <!-- Category Selector -->
            <div>
                <label
                    class="poppinsSemiBold ppv2-text-[#1A1F37] ppv2-mb-3 ppv2-block"
                    :style="{ fontSize: normalizeFontSize(16) }"
                >
                    {{ $t('experiences.createPopup.categoryLabel') }}
                </label>
                <select
                    v-model="form.prestation_category_id"
                    class="ppv2-w-full ppv2-h-[50px] ppv2-px-4 ppv2-rounded-xl ppv2-border ppv2-border-[#E2E8F0] ppv2-bg-white poppinsRegular"
                    :style="{ fontSize: normalizeFontSize(14) }"
                    required
                >
                    <option value="" disabled>{{ $t('experiences.createPopup.selectCategory') }}</option>
                    <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                        {{ cat.name }}
                    </option>
                </select>
                <p v-if="errors.prestation_category_id" class="ppv2-text-red-500 ppv2-text-xs ppv2-mt-1">
                    {{ errors.prestation_category_id[0] }}
                </p>
            </div>

            <!-- Name -->
            <CustomInput
                v-model="form.name"
                name="name"
                :label="$t('experiences.createPopup.nameLabel')"
                :placeholder="$t('experiences.createPopup.namePlaceholder')"
                required
                :customSizeLabel="16"
                customClassLabel="poppinsSemiBold ppv2-text-[#1A1F37]"
                :errorText="errors.name ? errors.name[0] : ''"
                :state="errors.name ? 'error' : 'default'"
                @input="onNameInput"
            />
        </div>

        <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-8 ppv2-mt-6">
            <!-- Slug -->
            <CustomInput
                v-model="form.slug"
                name="slug"
                :label="$t('experiences.createPopup.slugLabel')"
                :placeholder="$t('experiences.createPopup.slugPlaceholderSub')"
                :customSizeLabel="16"
                customClassLabel="poppinsSemiBold ppv2-text-[#1A1F37]"
                :errorText="errors.slug ? errors.slug[0] : ''"
                state="disabled"
            />

            <!-- Color -->
            <div>
                <label
                    class="poppinsSemiBold ppv2-text-[#1A1F37] ppv2-mb-3 ppv2-block"
                    :style="{ fontSize: normalizeFontSize(16) }"
                >
                    {{ $t('experiences.createPopup.colorLabel') }}
                </label>
                <div class="ppv2-flex ppv2-items-center ppv2-gap-3">
                    <input
                        type="color"
                        v-model="form.color"
                        class="ppv2-w-[40px] ppv2-h-[40px] ppv2-mb-[20px] ppv2-rounded-lg ppv2-border-none ppv2-cursor-pointer"
                    />
                    <CustomInput v-model="form.color" name="color" placeholder="#FFFFFF" customClass="ppv2-flex-1" />
                </div>
            </div>
        </div>

        <!-- Illustration -->
        <div class="ppv2-mt-6">
            <label class="poppinsSemiBold ppv2-text-[#1A1F37] ppv2-mb-3 ppv2-block" :style="{ fontSize: normalizeFontSize(16) }">
                {{ $t('experiences.createPopup.illustrationLabel') }}
            </label>
            <CustomInputUpload
                v-model="form.image"
                @input="onImageUpload"
                :description="$t('experiences.createPopup.chooseImage')"
                :errorText="errors.image ? errors.image[0] : ''"
                :state="errors.image ? 'error' : 'default'"
            />
        </div>

        <!-- Footer Actions -->
        <div class="ppv2-flex ppv2-justify-center ppv2-mt-10">
            <button
                type="submit"
                :disabled="isLoading"
                class="ppv2-bg-[#34216D] ppv2-text-white ppv2-px-16 ppv2-py-3 ppv2-rounded-xl poppinsSemiBold hover:ppv2-bg-[#2a1a57] ppv2-transition-all disabled:ppv2-opacity-50 ppv2-shadow-lg"
                :style="{ fontSize: normalizeFontSize(16) }"
            >
                {{ $t('experiences.createPopup.validate') }}
            </button>
        </div>
    </form>
</template>

<script>
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import axios from 'axios'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'PrestationSubcategoryForm',
    components: {
        CustomInput,
        CustomInputUpload,
    },
    props: {
        editItem: {
            type: Object,
            default: null,
        },
        initialCategoryId: {
            type: [Number, String],
            default: '',
        },
    },
    data() {
        return {
            isLoading: false,
            categories: [],
            form: {
                name: '',
                slug: '',
                prestation_category_id: this.initialCategoryId || '',
                color: '#7D2DBD',
                image: null,
            },
            errors: {},
        }
    },
    computed: {
        isEdit() {
            return !!this.editItem
        },
    },
    async created() {
        await this.fetchCategories()
        if (this.editItem) {
            this.form.name = this.editItem.name || ''
            this.form.slug = this.editItem.slug || ''
            this.form.prestation_category_id = this.editItem.category_id || ''
            this.form.color = this.editItem.color || '#7D2DBD'
            this.form.image = this.editItem.image || null
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchCategories() {
            try {
                const response = await axios.get('/api/v3/prestation_category/list', {
                    params: { itemsPerPage: 100 },
                })
                this.categories = response.data.data
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        onNameInput(val) {
            if (val && !this.isEdit) {
                this.form.slug = this.generateSlug(val)
            }
        },
        generateSlug(text) {
            return text
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)+/g, '')
        },
        validateForm() {
            const errors = {}
            if (!this.form.name || this.form.name.trim() === '') {
                errors.name = [this.$t('experiences.createPopup.nameRequired')]
            }
            if (!this.form.prestation_category_id) {
                errors.prestation_category_id = [this.$t('experiences.createPopup.categoryRequired')]
            }
            return errors
        },
        async submit() {
            this.errors = this.validateForm()

            if (Object.keys(this.errors).length > 0) {
                const firstError = Object.values(this.errors)[0][0]
                this.$toast?.error(firstError)
                return
            }

            this.isLoading = true
            this.errors = {}

            try {
                const formData = new FormData()
                formData.append('name', this.form.name)
                formData.append('slug', this.form.slug)
                formData.append('prestation_category_id', this.form.prestation_category_id)
                formData.append('color', this.form.color)

                if (this.form.image instanceof File) {
                    formData.append('image', this.form.image)
                }

                let endpoint = ''
                if (this.isEdit) {
                    endpoint = `/api/v3/prestation_subcategory/update/${this.editItem.id}`
                    formData.append('_method', 'PATCH')
                } else {
                    endpoint = '/api/v3/prestation_subcategory/store'
                }

                const response = await axios.post(endpoint, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })

                if (response.data.success) {
                    this.$toast?.success(
                        this.isEdit
                            ? response.data.message || this.$t('experiences.createPopup.updateSuccess')
                            : response.data.message || this.$t('experiences.createPopup.createSuccess')
                    )
                    this.$emit('success', response.data.data)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
                console.error('Submit error:', error)
                if (error.response && error.response.status === 422) {
                    this.errors = error.response.data.errors
                    const firstError = Object.values(this.errors)[0][0]
                    this.$toast?.error(firstError)
                } else {
                    this.$toast?.error(this.$t('experiences.createPopup.errorOccurred'))
                }
            } finally {
                this.isLoading = false
            }
        },
        onImageUpload(file) {
            this.form.image = file
        },
    },
}
</script>
